import { useRef } from 'react'

import { Text, useHealthToolsCache } from '@hhgtech/hhg-components'
import { useIntl } from 'react-intl'

import WarningIcon from '@/public/images/warning.svg'
import { AdBlock } from 'components/molecules/adBlock'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/fixedBottomAdBlock'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import { HealthToolLayout } from 'components/templates/healthToolLayout'
import { OvulationInfo } from 'components/templates/ovulationCalculator/ovulationInfo'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { BabyPoopPage } from 'interfaces/types'
import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'
import { fastdom } from 'utils/fastdom'

import {
  StyledBabyPoopContentWarning,
  StyledBabyPoopIntroduce,
  StyledBabyPoopIntroduceMainColumn,
  StyledBabyPoopIntroduceSubColumn,
  StyledBabyPoopListDescription,
  StyledBabyPoopListGrid,
} from './index.styled'
import { BabyPoopHeader, BabyPoopListItem } from '../components'
import {
  StyledBabyPoopInfo,
  StyledBabyPoopIntroduceParsedContent,
} from '../components/index.styled'

type Props = {
  babyPoopPage: BabyPoopPage
  isVN: boolean
}

const BabyPoopIntroduce = ({ babyPoopPage, isVN }: Props) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const { locale, asPath } = useRouterExtend()
  const warning = useRef<HTMLDivElement>(null)
  const { getHealthToolLink } = useHealthToolsCache(locale)

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, asPath, [
      'health-tools/baby-poop',
    ]) || {
      result: 'HealthTools',
      redundant: true,
    },
  })

  const pageItem = [
    {
      label: f({ id: 'healthtools.title' }),
      value: `/health-tools`,
    },
    {
      label: f({
        id: 'babyPoop.breadcrumbs',
        defaultMessage: 'Baby Poop',
      }),
      value: `${getHealthToolLink(HEALTH_TOOL_TYPE.BABY_POOP)}`,
    },
  ]

  const enabledAd = !!babyPoopPage?.enable_ad
  return (
    <StyledBabyPoopIntroduce>
      {!isMobile && isVN && <HealthToolBreadcrumb pageItem={pageItem} />}
      <BabyPoopHeader
        isVN={isVN}
        pageItem={pageItem}
        babyPoopPage={babyPoopPage}
        onClickFindOut={() => {
          fastdom.measure(() => {
            if (warning.current) {
              window.scrollTo({
                top: warning.current.getBoundingClientRect().top - 80,
                behavior: 'smooth',
              })
            }
          })
        }}
      />
      <HealthToolLayout>
        <HealthToolLayout.LeftColumn>
          <StyledBabyPoopIntroduceMainColumn>
            <StyledBabyPoopIntroduceParsedContent
              dangerouslySetInnerHTML={{
                __html: babyPoopPage.content,
              }}
            />
            {isMobile && enabledAd && (
              <div className="top-ad">
                <AdBlock
                  adUnitPath={adUnitPath}
                  size={[
                    [300, 250],
                    [300, 600],
                  ]}
                  divId={'div-gpt-ad-midbanner1-mobile'}
                  pos={['MidBanner1']}
                />
              </div>
            )}

            {babyPoopPage.toolGuideline && (
              <>
                <Text size="s1" type="semiBold" className="mt-6">
                  {babyPoopPage.toolGuideline.title}
                </Text>
                <StyledBabyPoopListDescription
                  className="mt-6"
                  dangerouslySetInnerHTML={{
                    __html: babyPoopPage.toolGuideline.description,
                  }}
                />
              </>
            )}

            <StyledBabyPoopContentWarning ref={warning} className="mt-6">
              <WarningIcon />
              <Text size="s3" type="bold">
                {f({
                  id: 'babyPoop.contentWarningMessage',
                  defaultMessage: 'Content warning: Photosensitivity',
                })}
              </Text>
            </StyledBabyPoopContentWarning>

            <StyledBabyPoopListGrid className="mt-6">
              {babyPoopPage.result.map((item) => (
                <BabyPoopListItem
                  key={item.name}
                  item={item}
                  link={`${basePath}${getHealthToolLink(
                    HEALTH_TOOL_TYPE.BABY_POOP,
                  )}?type=${item.name}`}
                  wrapperAttributes={{
                    'data-event-category': 'Health Tool',
                    'data-event-action': 'Baby Stool Option Click',
                    'data-event-label': `https://${
                      domainLocales[locale]
                    }${basePath}${getHealthToolLink(
                      HEALTH_TOOL_TYPE.BABY_POOP,
                    )}`,
                  }}
                />
              ))}
            </StyledBabyPoopListGrid>

            {babyPoopPage.disclaimer && (
              <OvulationInfo
                heading={f({ id: 'ovulationPage.disclaimer' })}
                content={babyPoopPage.disclaimer}
              />
            )}

            {!!babyPoopPage.information?.length && (
              <StyledBabyPoopInfo
                heading={f({ id: 'tools.faqs' })}
                infos={babyPoopPage.information}
                className="faqs-block"
                withDisclaimer={!!babyPoopPage.disclaimer}
              />
            )}

            {isMobile && enabledAd && (
              <div className="bottom-ad">
                <AdBlock
                  adUnitPath={adUnitPath}
                  size={[300, 250]}
                  divId={'div-gpt-ad-endbanner-mobile'}
                  pos={['EndBanner']}
                />
              </div>
            )}
          </StyledBabyPoopIntroduceMainColumn>
        </HealthToolLayout.LeftColumn>
        <HealthToolLayout.RightColumn>
          {!isMobile && enabledAd ? (
            <StyledBabyPoopIntroduceSubColumn>
              <div className="top-ad">
                <AdBlock
                  adUnitPath={adUnitPath}
                  size={[
                    [300, 250],
                    [300, 600],
                  ]}
                  divId={'div-gpt-ad-midbanner1-desktop'}
                  pos={['MidBanner1']}
                />
              </div>
              <div className="mt-10 bottom-ad">
                <AdBlock
                  adUnitPath={adUnitPath}
                  size={[300, 600]}
                  divId={'div-gpt-ad-endbanner-desktop'}
                  pos={['EndBanner']}
                />
              </div>
            </StyledBabyPoopIntroduceSubColumn>
          ) : undefined}
        </HealthToolLayout.RightColumn>
      </HealthToolLayout>
      {isMobile && <HealthToolBreadcrumb pageItem={pageItem} />}

      {enabledAd && (
        <FixedBottomAdBlock
          adUnitPath={adUnitPath}
          className="article-bottom-ad"
          isMobile={isMobile}
        />
      )}
    </StyledBabyPoopIntroduce>
  )
}

export { BabyPoopIntroduce }
